import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { axiosInstance } from '../../helpers'
import {
    SetupPackDetail,
    propertyLeasingSeasonConfigKey,
} from './useLeasingSeasonConfigApi'

export const GUEST_CARD_PACKS_KEY = 'property-guest-card-packs'
export const SIGNABLE_DOC_SETS_KEY = 'property-signable-doc-sets'
export const FEE_POLICIES_KEY = 'property-fee-policies'
export const CONDITIONAL_APPROVALS_KEY = 'property-conditional-approvals'
export const PROPERTY_DATA_PACKS_KEY = 'property-data-packs'

export const guestCardPacksKey = (apartmentId: number | null | undefined) =>
    [GUEST_CARD_PACKS_KEY, apartmentId] as const

export const signableDocSetsKey = (apartmentId: number | null | undefined) =>
    [SIGNABLE_DOC_SETS_KEY, apartmentId] as const

export const feePoliciesKey = (apartmentId: number | null | undefined) =>
    [FEE_POLICIES_KEY, apartmentId] as const

export const conditionalApprovalsKey = (
    apartmentId: number | null | undefined,
) => [CONDITIONAL_APPROVALS_KEY, apartmentId] as const

export const propertyDataPacksKey = (apartmentId: number | null | undefined) =>
    [PROPERTY_DATA_PACKS_KEY, apartmentId] as const

type NestedPackField =
    | 'guest_card_pack'
    | 'signable_doc_set'
    | 'fee_policy'
    | 'conditional_approvals'
    | 'property_data_pack'

function usePatchSetupPack(
    apartmentId: number | null | undefined,
    pathSegment: string,
    listKey: readonly unknown[],
    nestedField: NestedPackField,
    leasingSeasonConfigId?: number | null,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            packId: number
            name?: string
            description?: string
            definition?: Record<string, unknown>
            amount?: string | number
            currency?: string
        }) => {
            const { packId, ...body } = payload
            const { data } = await axiosInstance.patch<SetupPackDetail>(
                `/company/apartment/${apartmentId}/${pathSegment}/${packId}/`,
                body,
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: listKey })
            if (apartmentId == null || leasingSeasonConfigId == null) return
            queryClient.setQueryData(
                propertyLeasingSeasonConfigKey(
                    apartmentId,
                    leasingSeasonConfigId,
                ),
                (old: Record<string, unknown> | undefined) => {
                    if (!old) return old
                    return {
                        ...old,
                        [nestedField]: data,
                        [`${nestedField}_id`]: data.id,
                    }
                },
            )
        },
    })
}

export function useGuestCardPacks(apartmentId: number | null | undefined) {
    return useQuery({
        queryKey: guestCardPacksKey(apartmentId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<SetupPackDetail[]>(
                `/company/apartment/${apartmentId}/guest-card-packs/`,
            )
            return data
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function useSignableDocSets(apartmentId: number | null | undefined) {
    return useQuery({
        queryKey: signableDocSetsKey(apartmentId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<SetupPackDetail[]>(
                `/company/apartment/${apartmentId}/signable-doc-sets/`,
            )
            return data
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function useFeePolicies(apartmentId: number | null | undefined) {
    return useQuery({
        queryKey: feePoliciesKey(apartmentId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<SetupPackDetail[]>(
                `/company/apartment/${apartmentId}/fee-policies/`,
            )
            return data
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function useConditionalApprovalsList(
    apartmentId: number | null | undefined,
) {
    return useQuery({
        queryKey: conditionalApprovalsKey(apartmentId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<SetupPackDetail[]>(
                `/company/apartment/${apartmentId}/conditional-approvals/`,
            )
            return data
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function usePropertyDataPacks(apartmentId: number | null | undefined) {
    return useQuery({
        queryKey: propertyDataPacksKey(apartmentId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<SetupPackDetail[]>(
                `/company/apartment/${apartmentId}/property-data-packs/`,
            )
            return data
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function useSaveGuestCardPack(
    apartmentId: number | null | undefined,
    leasingSeasonConfigId?: number | null,
) {
    return usePatchSetupPack(
        apartmentId,
        'guest-card-packs',
        guestCardPacksKey(apartmentId),
        'guest_card_pack',
        leasingSeasonConfigId,
    )
}

export function useSaveSignableDocSet(
    apartmentId: number | null | undefined,
    leasingSeasonConfigId?: number | null,
) {
    return usePatchSetupPack(
        apartmentId,
        'signable-doc-sets',
        signableDocSetsKey(apartmentId),
        'signable_doc_set',
        leasingSeasonConfigId,
    )
}

export function useSaveFeePolicy(
    apartmentId: number | null | undefined,
    leasingSeasonConfigId?: number | null,
) {
    return usePatchSetupPack(
        apartmentId,
        'fee-policies',
        feePoliciesKey(apartmentId),
        'fee_policy',
        leasingSeasonConfigId,
    )
}

export function useSaveConditionalApprovals(
    apartmentId: number | null | undefined,
    leasingSeasonConfigId?: number | null,
) {
    return usePatchSetupPack(
        apartmentId,
        'conditional-approvals',
        conditionalApprovalsKey(apartmentId),
        'conditional_approvals',
        leasingSeasonConfigId,
    )
}

export function useSavePropertyDataPack(
    apartmentId: number | null | undefined,
    leasingSeasonConfigId?: number | null,
) {
    return usePatchSetupPack(
        apartmentId,
        'property-data-packs',
        propertyDataPacksKey(apartmentId),
        'property_data_pack',
        leasingSeasonConfigId,
    )
}

type CreatePackPath =
    | 'guest-card-packs'
    | 'signable-doc-sets'
    | 'fee-policies'
    | 'conditional-approvals'
    | 'property-data-packs'

function useCreateSetupPack(
    apartmentId: number | null | undefined,
    path: CreatePackPath,
    listKey: readonly unknown[],
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            name: string
            description?: string
            definition?: Record<string, unknown>
            amount?: string | number
            currency?: string
        }) => {
            const { data } = await axiosInstance.post<SetupPackDetail>(
                `/company/apartment/${apartmentId}/${path}/`,
                payload,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: listKey })
        },
    })
}

export function useCreateGuestCardPack(apartmentId: number | null | undefined) {
    return useCreateSetupPack(
        apartmentId,
        'guest-card-packs',
        guestCardPacksKey(apartmentId),
    )
}

export function useCreateSignableDocSet(
    apartmentId: number | null | undefined,
) {
    return useCreateSetupPack(
        apartmentId,
        'signable-doc-sets',
        signableDocSetsKey(apartmentId),
    )
}

export function useCreateFeePolicy(apartmentId: number | null | undefined) {
    return useCreateSetupPack(
        apartmentId,
        'fee-policies',
        feePoliciesKey(apartmentId),
    )
}

export function useCreateConditionalApprovals(
    apartmentId: number | null | undefined,
) {
    return useCreateSetupPack(
        apartmentId,
        'conditional-approvals',
        conditionalApprovalsKey(apartmentId),
    )
}

export function useCreatePropertyDataPack(
    apartmentId: number | null | undefined,
) {
    return useCreateSetupPack(
        apartmentId,
        'property-data-packs',
        propertyDataPacksKey(apartmentId),
    )
}

export function parseSetupPackId(
    id: string | number | null | undefined,
): number | null {
    if (typeof id === 'number') {
        return Number.isInteger(id) && id > 0 ? id : null
    }
    if (typeof id === 'string' && /^\d+$/.test(id)) {
        return Number(id)
    }
    return null
}

type CopyablePackPath =
    | 'guest-card-packs'
    | 'signable-doc-sets'
    | 'fee-policies'
    | 'conditional-approvals'
    | 'property-data-packs'

export function useCopySetupPack(apartmentId: number | null | undefined) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            path: CopyablePackPath
            packId: number
            name?: string
        }) => {
            const { data } = await axiosInstance.post<SetupPackDetail>(
                `/company/apartment/${apartmentId}/${payload.path}/${payload.packId}/copy/`,
                payload.name ? { name: payload.name } : {},
            )
            return data
        },
        onSuccess: (_data, vars) => {
            const listKey =
                vars.path === 'guest-card-packs'
                    ? guestCardPacksKey(apartmentId)
                    : vars.path === 'signable-doc-sets'
                    ? signableDocSetsKey(apartmentId)
                    : vars.path === 'fee-policies'
                    ? feePoliciesKey(apartmentId)
                    : vars.path === 'property-data-packs'
                    ? propertyDataPacksKey(apartmentId)
                    : conditionalApprovalsKey(apartmentId)
            queryClient.invalidateQueries({ queryKey: listKey })
        },
    })
}
